import { Flex } from "@mantine/core";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { colors } from "#app/theme/v2/colors";
import { radius } from "#app/theme/v2/radius";
import { spacing } from "#app/theme/v2/spacing";
import { Button } from "#components/atoms/Button/Button";
import { Text } from "#components/atoms/Typographies/Text";
import { Select } from "#components/molecules/Select/Select";
import { ProcessStepOption } from "#modules/onboardingV2/domain/getProcessStepOptions";

const OPTION_ROW_STYLE: React.CSSProperties = {
  display: "flex",
  alignItems: "baseline",
  gap: spacing[1],
  width: 0,
  minWidth: "100%",
};

const OPTION_LABEL_STYLE: React.CSSProperties = {
  flexShrink: 0,
  maxWidth: "100%",
};

const OPTION_DESCRIPTION_STYLE: React.CSSProperties = {
  minWidth: 0,
};

type AddDefaultProcessStepRowProps = {
  position: number;
  options: ProcessStepOption[];
  isPending: boolean;
  onCancel: () => void;
  onAdd: (option: ProcessStepOption) => void;
};

type ProcessStepSelectOptionProps = {
  option: ProcessStepOption;
};

const ProcessStepSelectOption = ({ option }: ProcessStepSelectOptionProps) => {
  return (
    <div style={OPTION_ROW_STYLE}>
      <Text size="small" component="span" truncate style={OPTION_LABEL_STYLE}>
        {option.label}
      </Text>
      {option.description ? (
        <Text
          size="tiny"
          component="span"
          color={colors.text.secondaryDefault}
          truncate
          style={OPTION_DESCRIPTION_STYLE}
        >
          {option.description}
        </Text>
      ) : null}
    </div>
  );
};

export const AddDefaultProcessStepRow = ({
  position,
  options,
  isPending,
  onCancel,
  onAdd,
}: AddDefaultProcessStepRowProps) => {
  const { t } = useTranslation();
  const [selectedValue, setSelectedValue] = useState<string | null>(null);
  const [hasError, setHasError] = useState(false);
  const selectedOption = options.find((option) => option.value === selectedValue) ?? null;
  const selectPlaceholder = t("onboardingV2.defaultProcessSteps.selectPlaceholder");

  const handleChange = (value: string | null) => {
    setSelectedValue(value);
    if (value) {
      setHasError(false);
    }
  };

  const handleAdd = () => {
    if (!selectedOption) {
      setHasError(true);
      return;
    }
    onAdd(selectedOption);
  };

  return (
    <Flex align="center" gap={spacing[4]} h={spacing[16]} px={spacing[6]} w="100%">
      <Flex
        align="center"
        justify="center"
        w={spacing[8]}
        h={spacing[8]}
        style={{
          flexShrink: 0,
          backgroundColor: colors.bg.elevation.tertiary,
          borderRadius: radius.md,
        }}
      >
        <Text size="small" type="medium" color={colors.text.secondaryDefault}>
          {position}
        </Text>
      </Flex>

      <Flex style={{ flex: 1, minWidth: 0 }}>
        <Select
          value={selectedValue}
          onChange={handleChange}
          placeholder={selectPlaceholder}
          aria-label={selectPlaceholder}
          searchable={false}
          error={hasError}
          renderValue={selectedOption ? <ProcessStepSelectOption option={selectedOption} /> : null}
          renderOption={({ option }) => {
            const processStepOption = options.find((item) => item.value === option.value);

            return processStepOption ? (
              <ProcessStepSelectOption option={processStepOption} />
            ) : (
              option.label
            );
          }}
          data={[
            {
              group: t("onboardingV2.defaultProcessSteps.selectHeader"),
              items: options.map((option) => ({ value: option.value, label: option.label })),
            },
          ]}
        />
      </Flex>

      <Flex align="center" gap={spacing[2]} style={{ flexShrink: 0 }}>
        <Button variant="secondary" size="md" onClick={onCancel} disabled={isPending}>
          {t("common.cancel")}
        </Button>
        <Button variant="primary" size="md" onClick={handleAdd} loading={isPending}>
          {t("common.add")}
        </Button>
      </Flex>
    </Flex>
  );
};
